import { useCallback, useEffect, useState } from 'react'

import { LocalStorage, loadStorage } from '@/common/util/storage'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import { fetchRest } from '@/common/api/fetcher'

/**
 * 投稿フォームのデザイン設定＋実物プレビュー。
 *
 * 変えられるのは選択式の項目に絞っている——色・ボタン文言・評価の絵柄と
 * 大きさ・写真添付の可否と枚数。自由CSSを許すと、崩れたフォームが
 * そのまま購入者に出る。選択肢と色なら壊しようがない。
 *
 * メールの枠（レビューを書くボタン）も**同じアクセントカラー**が使われる。
 * 別々に設定できると、メールとフォームで色がちぐはぐになり、
 * 別サービスに飛ばされたように見えるため。
 *
 * ロゴ・ブランド名は「Instagram UGC ＞ Instagram設定」のブランド情報から。
 * プレビューは実フォームと同じ組み立てを通す（別に持つと実物とずれる）。
 */

const input =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

type Design = {
  accent_color: string
  bg_color: string
  button_label: string
  /** 評価の絵柄。選択肢に限る（自由入力だと崩れた文字がフォームに出る） */
  rating_icon: 'star' | 'heart' | 'circle'
  rating_size: 's' | 'm' | 'l'
  photo_enabled: boolean
  photo_max: number
}

const RATING_ICONS = [
  { value: 'star', label: '★ 星' },
  { value: 'heart', label: '♥ ハート' },
  { value: 'circle', label: '● 丸' }
] as const

const RATING_SIZES = [
  { value: 's', label: '小' },
  { value: 'm', label: '中' },
  { value: 'l', label: '大' }
] as const

const authHeader = () => ({
  Authorization: `Bearer ${loadStorage(LocalStorage.AUTH_TOKEN) || ''}`
})

const FormDesignEditor = ({ siteId }: { siteId: number }) => {
  const [design, setDesign] = useState<Design | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)

  const loadPreview = useCallback(() => {
    // ここだけは HTML（text/html）が返るので fetchRest（JSON前提）を通せない
    fetch(`/api/sites/${siteId}/ugc/review-form/preview-html`, { headers: authHeader() })
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then(setPreviewHtml)
      .catch(() => setPreviewHtml(null))
  }, [siteId])

  useEffect(() => {
    fetchRest<Design>(`/api/sites/${siteId}/ugc/form-design`)
      .then(setDesign)
      .catch(() => setMessage('デザイン設定を取得できませんでした'))
    loadPreview()
  }, [siteId, loadPreview])

  const onSave = () => {
    if (!design) return
    setSaving(true)
    setMessage(null)
    fetchRest<Design>(`/api/sites/${siteId}/ugc/form-design`, {
      method: 'PUT',
      body: JSON.stringify(design)
    })
      .then((saved) => {
        setDesign(saved)
        setMessage('保存しました。メールのボタン・評価の絵柄にも同じ設定が使われます。')
        loadPreview() // 保存した設定で実物プレビューを更新する
      })
      .catch(() => setMessage('保存できませんでした'))
      .finally(() => setSaving(false))
  }

  return (
    <Area>
      <Heading subText='色・評価の絵柄と大きさ・写真添付・ボタン文言を設定します。ロゴとブランド名は「Instagram UGC ＞ Instagram設定」で設定します。'>
        フォームのデザイン
      </Heading>
      <AreaContent>
        {design === null ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : (
          <div className='flex flex-col gap-4'>
            <div className='flex flex-wrap items-end gap-6'>
              <label className='flex flex-col gap-1'>
                <Text size={12}>アクセントカラー（ボタン・星）</Text>
                <div className='flex items-center gap-2'>
                  <input
                    type='color'
                    value={design.accent_color}
                    onChange={(e) => setDesign({ ...design, accent_color: e.target.value })}
                    className='h-9 w-14 cursor-pointer rounded border border-border-input'
                  />
                  <Text size={12} color={TextColor.TERTIARY}>
                    {design.accent_color}
                  </Text>
                </div>
              </label>
              <label className='flex flex-col gap-1'>
                <Text size={12}>背景色</Text>
                <div className='flex items-center gap-2'>
                  <input
                    type='color'
                    value={design.bg_color}
                    onChange={(e) => setDesign({ ...design, bg_color: e.target.value })}
                    className='h-9 w-14 cursor-pointer rounded border border-border-input'
                  />
                  <Text size={12} color={TextColor.TERTIARY}>
                    {design.bg_color}
                  </Text>
                </div>
              </label>
              <label className='flex flex-col gap-1'>
                <Text size={12}>送信ボタンの文言</Text>
                <input
                  className={`${input} w-56`}
                  value={design.button_label}
                  maxLength={30}
                  onChange={(e) => setDesign({ ...design, button_label: e.target.value })}
                />
              </label>
            </div>

            <div className='flex flex-wrap items-end gap-6'>
              <label className='flex flex-col gap-1'>
                <Text size={12}>評価の絵柄</Text>
                <select
                  className={input}
                  value={design.rating_icon}
                  onChange={(e) =>
                    setDesign({ ...design, rating_icon: e.target.value as Design['rating_icon'] })
                  }
                >
                  {RATING_ICONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className='flex flex-col gap-1'>
                <Text size={12}>絵柄の大きさ</Text>
                <select
                  className={input}
                  value={design.rating_size}
                  onChange={(e) =>
                    setDesign({ ...design, rating_size: e.target.value as Design['rating_size'] })
                  }
                >
                  {RATING_SIZES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className='flex flex-col gap-1'>
                <Text size={12}>写真の添付</Text>
                <select
                  className={input}
                  value={design.photo_enabled ? String(design.photo_max) : 'off'}
                  onChange={(e) => {
                    const v = e.target.value
                    if (v === 'off') setDesign({ ...design, photo_enabled: false })
                    else setDesign({ ...design, photo_enabled: true, photo_max: Number(v) })
                  }}
                >
                  <option value='off'>受け付けない</option>
                  <option value='1'>1枚まで</option>
                  <option value='2'>2枚まで</option>
                  <option value='3'>3枚まで</option>
                  <option value='5'>5枚まで</option>
                </select>
              </label>
              <Button color={ButtonColor.PRIMARY} loading={saving} disabled={saving} onClick={onSave}>
                保存してプレビューに反映
              </Button>
            </div>
            {message && (
              <Text size={12} color={TextColor.SECONDARY}>
                {message}
              </Text>
            )}

            <div>
              <Text size={12} weight='bold'>
                実物プレビュー（送信はできません）
              </Text>
              <div className='mt-2 overflow-x-auto rounded-lg border border-border-primary'>
                {previewHtml === null ? (
                  <div className='p-4'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      プレビューを読み込み中...
                    </Text>
                  </div>
                ) : (
                  <iframe
                    title='投稿フォームのプレビュー'
                    srcDoc={previewHtml}
                    sandbox='allow-scripts'
                    className='h-[640px] w-full min-w-[560px] bg-white'
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default FormDesignEditor
